import type { SaveToProjectType } from '@eureka/shared/save-to-project/services';
import type { ShareRole } from '@eureka/services/src/types/share';

export enum ReportCatalogStatus {
  PENDING = 'pending',
  DONE = 'done',
  WAITING = 'waiting',
  ERROR = 'error',
}

export enum EStreamStatus {
  PROCESSING = 'PROCESSING',
  FINISH = 'FINISH',
  FAILED = 'FAILED',
}

export interface ReportCatalog {
  id?: string;
  content?: string;
  title?: string;
  description?: string;
  status?: ReportCatalogStatus;
}

export interface Outline {
  parent_topic?: string;
  index?: string;
}

export interface ReportDetail {
  id: string;
  title: string;
  description: string;
  project_id: string;
  time_saved: number;
  completed_time: number;
  sections: Outline[];
}
export interface ReportTitle {
  title?: {
    text?: string;
  };
}
export interface ReportOutline {
  outline?: Outline[];
}

export interface ReportContent {
  phrase?: {
    label_text?: string;
    text?: string;
    index?: string;
  };
}

export interface ReportInfo {
  spend_time?: number;
  time_saved?: number;
}

export interface ReportData<T> {
  status?: 'PROCESSING' | 'FINISH' | 'FAILED';
  message?: T;
}
export type ReportResponse = ReportTitle | ReportOutline | ReportContent | ReportInfo;
export type ReportResponseData = ReportData<ReportResponse>;

export interface ReportUpdateParams {
  projectId: string;
  index?: string;
  text?: string;
  title?: string;
}

export interface IProjectDetail {
  work_project_id?: string;
  title?: string;
  item_count?: number;
  created_at?: string;
  updated_at?: string;
  time_saved?: string;
  graph?: boolean; // 是否有查看知识图谱权限
  ls_role?: boolean; // 是否有LS的权限，有的话文件处理会增加 ner 和 ocsr 的步骤
  share_type?: ShareRole;
  share_count?: number;
}

export interface INodeData {
  id?: string;
  props?: {
    id?: string;
    title?: string;
    type?: string;
  };
  // 前端数据
  data?: {
    id?: string;
    title?: string;
    questions?: string[];
    type?: string;
    fe_count?: number;
    fe_target_count?: number;
    fe_size?: number;
    fe_virtual?: boolean;
    fe_if_leaf?: boolean;
    fe_degree?: number;
    description?: string;
  };
}

export enum EdgeType {
  USED = 'Used-for',
  INCLUDE = 'Include-in',
}

export interface IEdgeData {
  id?: string;
  src?: string;
  dst?: string;
  source?: string;
  target?: string;
  name?: string;
  props?: {
    description?: string;
    display_description_cn?: string;
    display_description_en?: string;
    id?: string;
    type?: string;
    edge_type?: EdgeType;
  };
  // 前端数据
  data?: {
    id?: string;
    type?: string;
    description?: string;
    fe_virtual?: boolean;
  };
}

export interface IGraphData {
  nodes?: INodeData[];
  edges?: IEdgeData[];
  src_node_linked_count?: Record<string, number>;
  dst_node_linked_count?: Record<string, number>;
}

export interface IDocumentResponse {
  page?: number;
  limit?: number;
  total?: number;
  rows?: IDocument[];
}

export interface IDocument {
  item_id?: string;
  work_project_id?: string;
  title?: string;
  content?: string;
  htmlContent?: string;
  date?: string;
  tags?: Array<{ name?: string; id?: string }>;
  business_id?: string;
  business_node_id?: string;
  business_type?: SaveToProjectType;
  created_at?: string;
  item_process_status?: IItemStatus;
}

export enum IUploadFileStatus {
  WAITING = 'waiting',
  UPLOADING = 'uploading',
  SUCCESS = 'success',
  ERROR = 'error',
}
export enum IUploadFileErrorCode {
  TYPE_ERROR = 'type-error',
  SIZE_ERROR = 'size-error',
  COUNT_ERROR = 'count-error',
}

export interface IUploadFile extends File {
  id: string;
  uploadStatus?: IUploadFileStatus;
  errorCode?: IUploadFileErrorCode;
}

export interface IFile extends IDocument {
  content_format_type?: 'pdf' | 'word';
  content?: string;
  file_path?: string;
  is_owner?: boolean;
  icon_file_path?: string;
}
// 文件上传相关状态
export type FileUploadStatus = 'token_limit_exceeded' | 'file_upload_failed';

// 文件解析相关状态
export type FileParseStatus =
  | 'file_parse_pending'
  | 'file_parse_processing'
  | 'file_parse_success'
  | 'file_parse_failed';

// 数据提取相关状态
export type DataExtractStatus =
  | 'data_extract_pending'
  | 'data_extract_processing'
  | 'data_extract_success'
  | 'data_extract_failed';

// 知识图谱生成相关状态
export type KGGenerateStatus =
  | 'kg_generate_pending'
  | 'kg_generate_processing'
  | 'kg_generate_success'
  | 'kg_generate_failed';

export type AdvancedProcessingStatus =
  | 'advanced_processing_pending'
  | 'advanced_processing_processing'
  | 'advanced_processing_success'
  | 'advanced_processing_failed';

// 无kg权限时走到data_extract_success状态，增加一个无权限状态
export type NoKGPermissionStatus = 'data_extract_success_no_kg';

/**
 * ner nor 处理状态
 * - ner_pending: 待实体识别
 * - ner_processing: 实体识别中
 * - ner_success: 成功
 * - ner_failed: 失败
 */
export type NerNorProcessStatus = 'ner_pending' | 'ner_processing' | 'ner_success' | 'ner_failed';

/**
 * ocsr 处理状态
 * - ocsr_pending: 待ocr
 * - ocsr_processing: ocr中
 * - ocsr_success: ocr成功
 * - ocsr_failed: ocr失败
 */
export type OcsrProcessStatus = 'ocsr_pending' | 'ocsr_processing' | 'ocsr_success' | 'ocsr_failed';

export type IItemStatus =
  | FileUploadStatus
  | FileParseStatus
  | DataExtractStatus
  | KGGenerateStatus
  | NoKGPermissionStatus
  | NerNorProcessStatus
  | OcsrProcessStatus
  | AdvancedProcessingStatus;

export interface IFileResponse {
  page?: number;
  limit?: number;
  total?: number;
  rows?: IFile[];
}

// 新增：推荐数据相关类型
export interface RecommendItem {
  business_id: string;
  business_node_id: string;
  business_type: SaveToProjectType;
  title: string;
  content: string;
  date?: string;
}

// 聚类推荐数据
export interface GroupedRecommendItem {
  topic_name: string;
  recommend_items: RecommendItem[];
}

// 推荐数据API响应
export interface RecommendResponse {
  grouped_recommend_items: GroupedRecommendItem[];
  recommend_items: RecommendItem[];
}

// 历史数据API响应
export interface HistoryResponse {
  recommend_items: RecommendItem[];
  has_record_history: boolean;
}

export interface CreateProjectWithItemsParams {
  item_list: {
    business_id: string;
    business_node_id: string;
    business_type: string;
    title: string;
    content: string;
  }[];
  work_project_name?: string;
}
